// Lead-capture adapter for the React-hosted Planapus pilot.
//
// Routes Ahmed's `submitLead(source, payload)` calls through the
// `window.__mp.submitLead(...)` bridge installed by leadsBridge.ts. That
// bridge calls Apollo Client's submitPilotLead mutation, which writes to
// the existing pilot_leads Firestore collection and triggers the founder
// email via SendGrid. No direct `/api/graphql` fetch (won't work under
// Firebase Hosting emulator at :5010 → Functions emulator at :5001).

import type { PilotLeadInput, PilotLeadKind } from "./leadsBridge";

export type LeadSource = "TALK_TO_US" | "REPORT_GATE" | "EMAIL_GATE";

const SOURCE_TO_KIND: Record<LeadSource, PilotLeadKind> = {
  TALK_TO_US: "FOUNDER_CHAT",
  REPORT_GATE: "ROI_EMAIL",
  EMAIL_GATE: "ROI_EMAIL",
};

/** Visitor-context blob carried alongside the lead. */
export interface LeadContext {
  intake?: {
    industry?: string;
    size?: string;
    function?: string;
  };
  process?: string;
}

/** Freeform shape submitted by the vanilla pilot modules. */
export interface LeadPayload {
  email?: string;
  name?: string;
  company?: string;
  role?: string;
  linkedin?: string;
  ops?: string[];
  opsOther?: string;
  goals?: string;
  stack?: string;
  context?: LeadContext;
}

export interface LeadSubmitResult {
  ok: boolean;
  leadId: string | null;
  error?: string;
}

interface SplitName {
  firstName: string | undefined;
  lastName: string | undefined;
}

function splitFullName(full: string | null | undefined): SplitName {
  if (!full || typeof full !== "string") return { firstName: undefined, lastName: undefined };
  const parts = full.trim().split(/\s+/);
  if (parts.length === 1) return { firstName: parts[0], lastName: undefined };
  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
}

// `notes` now carries only freeform residual context — the visitor's
// intake org profile and current process slug. linkedin/ops/opsOther/
// goals/stack travel as first-class structured fields on the mutation
// input (see submitLead below) so the admin console and the Apollo CRM
// write-back can read them without parsing a blob.
function packContextNotes(payload: LeadPayload | null | undefined): string {
  const parts: string[] = [];
  const ctx = payload?.context || {};
  const intake = ctx.intake || {};
  if (intake.industry || intake.size || intake.function) {
    parts.push(
      `Org: ${[intake.industry, intake.size, intake.function].filter(Boolean).join(" · ")}`,
    );
  }
  if (ctx.process) parts.push(`Process: ${ctx.process}`);
  return parts.join("\n\n");
}

export async function submitLead(
  source: LeadSource,
  payload: LeadPayload,
): Promise<LeadSubmitResult> {
  const kind: PilotLeadKind = SOURCE_TO_KIND[source] || "FOUNDER_CHAT";
  const email = (payload?.email || "").trim();
  if (!email) {
    return { ok: false, leadId: null, error: "missing-email" };
  }
  const bridge =
    typeof window !== "undefined" && window.__mp && window.__mp.submitLead;
  if (typeof bridge !== "function") {
    return { ok: false, leadId: null, error: "bridge-not-installed" };
  }
  const { firstName, lastName } = splitFullName(payload?.name);
  try {
    const ops =
      Array.isArray(payload?.ops) && payload.ops.length ? payload.ops : undefined;
    const input: PilotLeadInput = {
      kind,
      email,
      firstName,
      lastName,
      company: payload?.company || undefined,
      role: payload?.role || undefined,
      linkedin: payload?.linkedin || undefined,
      ops,
      opsOther: payload?.opsOther || undefined,
      goals: payload?.goals || undefined,
      stack: payload?.stack || undefined,
      notes: packContextNotes(payload) || undefined,
      simPayload: payload?.context?.process
        ? { scenarioId: payload.context.process, scenarioLabel: payload.context.process }
        : undefined,
    };
    const result = await bridge(input);
    if (typeof window !== "undefined" && window.__mp && typeof window.__mp.track === "function") {
      window.__mp.track("lead_dialog_submitted", { kind, source: source.toLowerCase() });
    }
    return { ok: !!result?.ok, leadId: result?.leadId ?? null };
  } catch (err) {
    return { ok: false, leadId: null, error: String(err) };
  }
}
